import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { ArrowRight, Bookmark, FilePenLine, History, MapPin, Orbit, UserRoundPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchDiscover, type DiscoverResponse } from "../api/discover";
import { fetchJoinedSpaces, type Space } from "../api/spaces";
import "./HomeSidebar.css";

type Coordinates = { latitude: number; longitude: number };
type Weather = {
    code: number;
    cloudCover: number;
    precipitation: number;
    isDay: boolean;
    sunrise: string | null;
    sunset: string | null;
    timezone: string;
};
type ActiveSpace = { id: string; title: string; lastPostAt: string };
type SkyPeriod = "morning" | "day" | "evening" | "night";
type SkyCondition = "clear" | "cloudy" | "rain" | "storm" | "snow";

const WEATHER_TTL_MS = 20 * 60 * 1000;
const weatherCache = new Map<string, { weather: Weather; expiresAt: number }>();

function useDesktopSidebar(active: boolean) {
    const [desktop, setDesktop] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 1280px)").matches);
    useEffect(() => {
        const query = window.matchMedia("(min-width: 1280px)");
        const update = () => setDesktop(query.matches);
        update();
        query.addEventListener("change", update);
        return () => query.removeEventListener("change", update);
    }, []);
    return active && desktop;
}

async function getWeather({ latitude, longitude }: Coordinates, signal: AbortSignal): Promise<Weather> {
    // A coarse location is sufficient for local weather and avoids sharing precise coordinates.
    const lat = latitude.toFixed(2);
    const lon = longitude.toFixed(2);
    const key = `${lat},${lon}`;
    const cached = weatherCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.weather;

    const url = new URL("https://api.open-meteo.com/v1/forecast");
    url.searchParams.set("latitude", lat);
    url.searchParams.set("longitude", lon);
    url.searchParams.set("current", "weather_code,cloud_cover,precipitation,is_day");
    url.searchParams.set("daily", "sunrise,sunset");
    url.searchParams.set("forecast_days", "1");
    url.searchParams.set("timezone", "auto");

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error("Local sky is unavailable.");
    const data = await response.json();
    if (!data.current || !Number.isFinite(Number(data.current.weather_code))) {
        throw new Error("Local sky is unavailable.");
    }
    const weather: Weather = {
        code: Number(data.current.weather_code),
        cloudCover: Number(data.current.cloud_cover) || 0,
        precipitation: Number(data.current.precipitation) || 0,
        isDay: data.current.is_day === 1,
        sunrise: data.daily?.sunrise?.[0] || null,
        sunset: data.daily?.sunset?.[0] || null,
        timezone: typeof data.timezone === "string" ? data.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    weatherCache.set(key, { weather, expiresAt: Date.now() + WEATHER_TTL_MS });
    return weather;
}

function localHour(date: Date, timezone?: string) {
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: timezone,
            hour: "numeric",
            minute: "numeric",
            hourCycle: "h23",
        }).formatToParts(date);
        return Number(parts.find((part) => part.type === "hour")?.value || 0)
            + Number(parts.find((part) => part.type === "minute")?.value || 0) / 60;
    } catch {
        return date.getHours() + date.getMinutes() / 60;
    }
}

function apiTimeHour(value: string | null) {
    const match = value?.match(/T(\d{2}):(\d{2})/);
    return match ? Number(match[1]) + Number(match[2]) / 60 : null;
}

function skyPeriod(date: Date, weather: Weather | null): SkyPeriod {
    const hour = localHour(date, weather?.timezone);
    const sunrise = apiTimeHour(weather?.sunrise || null);
    const sunset = apiTimeHour(weather?.sunset || null);
    if (weather) {
        if (!weather.isDay) return "night";
        if (sunrise !== null && sunset !== null) {
            if (hour < sunrise + 1.5) return "morning";
            if (hour > sunset - 1.25) return "evening";
        }
        return "day";
    }
    if (hour >= 5 && hour < 11) return "morning";
    if (hour >= 11 && hour < 17) return "day";
    if (hour >= 17 && hour < 20) return "evening";
    return "night";
}

function skyCondition(weather: Weather | null): SkyCondition {
    if (!weather) return "clear";
    if (weather.code >= 95) return "storm";
    if ((weather.code >= 71 && weather.code <= 77) || (weather.code >= 85 && weather.code <= 86)) return "snow";
    if ((weather.code >= 51 && weather.code <= 67) || (weather.code >= 80 && weather.code <= 82) || weather.precipitation > 0) return "rain";
    return weather.cloudCover >= 55 || weather.code >= 2 ? "cloudy" : "clear";
}

function greeting(date: Date, timezone?: string) {
    const hour = localHour(date, timezone);
    if (hour >= 5 && hour < 12) return "Good morning";
    if (hour >= 12 && hour < 17) return "Good afternoon";
    if (hour >= 17 && hour < 21) return "Good evening";
    return "Good night";
}

function localDateLabel(date: Date, timezone?: string) {
    const options: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short", year: "numeric" };
    try {
        return new Intl.DateTimeFormat(undefined, { ...options, timeZone: timezone }).format(date);
    } catch {
        return new Intl.DateTimeFormat(undefined, options).format(date);
    }
}

function SkyCloudLayer({ front }: { front: boolean }) {
    const layer = front ? "front" : "back";
    return (
        <div className={`bs-home-sidebar-cloud-track bs-home-sidebar-cloud-${layer}`}>
            {[0, 1].map((copy) => (
                <svg key={copy} viewBox="0 0 640 160" preserveAspectRatio="none">
                    <defs>
                        <linearGradient id={`bs-sidebar-cloud-${layer}-${copy}`} x2="0" y2="1">
                            <stop stopColor="var(--bs-sky-cloud-light)" />
                            <stop offset="1" stopColor="var(--bs-sky-cloud-shadow)" />
                        </linearGradient>
                    </defs>
                    <path d="M0 80 C30 48 65 43 97 61 C108 27 149 12 177 42 C206 24 235 34 247 65 C278 35 311 43 326 67 C352 32 394 30 416 64 C439 45 474 37 503 60 C533 24 575 28 594 58 C612 52 628 67 640 80 V160 H0 Z" fill={`url(#bs-sidebar-cloud-${layer}-${copy})`} />
                </svg>
            ))}
        </div>
    );
}

function SkyIllustration({ period, condition, cloudCover, animate }: {
    period: SkyPeriod;
    condition: SkyCondition;
    cloudCover: number;
    animate: boolean;
}) {
    const night = period === "night";
    const clouded = condition !== "clear";
    const cloudOpacity = Math.min(0.9, Math.max(condition === "storm" ? 0.8 : 0.4, cloudCover / 100));
    return (
        <div
            className="bs-home-sidebar-sky"
            data-period={period}
            data-condition={condition}
            data-motion={animate ? "running" : "paused"}
            style={{ "--bs-sky-cloud-opacity": cloudOpacity } as CSSProperties}
            aria-hidden="true"
        >
            <div className="bs-home-sidebar-sky-gradient" />
            <div className="bs-home-sidebar-sky-glow" />
            <div className="bs-home-sidebar-atmosphere" />
            <div className="bs-home-sidebar-horizon" />
            {night && !clouded && (
                <div className="bs-home-sidebar-stars">
                    {Array.from({ length: 12 }, (_, index) => <span key={index} style={{ left: `${(index * 29 + 7) % 97}%`, top: `${(index * 19 + 11) % 78}%`, animationDelay: `-${index * 0.41}s`, animationDuration: `${2.2 + index % 4 * 0.6}s` }} />)}
                </div>
            )}
            <div className="bs-home-sidebar-celestial">
                {night ? (
                    <svg className="bs-home-sidebar-moon" viewBox="0 0 24 24" fill="currentColor">
                        <defs>
                            <mask id="bs-sidebar-thin-crescent" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                                <circle cx="12" cy="12" r="9" fill="white" />
                                <circle cx="14.2" cy="9.8" r="10" fill="black" />
                            </mask>
                        </defs>
                        <circle cx="12" cy="12" r="9" mask="url(#bs-sidebar-thin-crescent)" />
                    </svg>
                ) : (
                    <><div className="bs-home-sidebar-sun-rays" /><div className="bs-home-sidebar-sun" /></>
                )}
            </div>
            <div className="bs-home-sidebar-clouds" data-ambient={!clouded ? "true" : undefined}>
                <SkyCloudLayer front={false} />
                <SkyCloudLayer front />
            </div>
            {(condition === "rain" || condition === "storm") && (
                <div className="bs-home-sidebar-rain">
                    {Array.from({ length: 28 }, (_, index) => <span key={index} data-depth={index % 3 === 0 ? "near" : "far"} style={{ left: `${(index * 37 + 3) % 102}%`, top: `${(index * 23) % 100}%`, animationDelay: `-${index * 0.17}s`, animationDuration: `${0.75 + index % 5 * 0.13}s` }} />)}
                </div>
            )}
            {condition === "storm" && <div className="bs-home-sidebar-lightning" />}
            {condition === "snow" && (
                <div className="bs-home-sidebar-snow">
                    {Array.from({ length: 16 }, (_, index) => <span key={index} style={{ left: `${(index * 31 + 5) % 100}%`, top: `${(index * 17) % 100}%`, animationDelay: `-${index * 0.43}s`, animationDuration: `${3.2 + index % 4 * 0.7}s` }} />)}
                </div>
            )}
            <div className="bs-home-sidebar-sky-scrim" />
        </div>
    );
}

function SkyGreeting({ active }: { active: boolean }) {
    const [now, setNow] = useState(() => new Date());
    const [coords, setCoords] = useState<Coordinates | null>(null);
    const [weather, setWeather] = useState<Weather | null>(null);
    const [status, setStatus] = useState("");
    const [locating, setLocating] = useState(false);
    const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== "hidden");

    useEffect(() => {
        const update = () => setPageVisible(document.visibilityState !== "hidden");
        document.addEventListener("visibilitychange", update);
        return () => document.removeEventListener("visibilitychange", update);
    }, []);

    useEffect(() => {
        if (!active) return;
        setNow(new Date());
        const timer = window.setInterval(() => setNow(new Date()), 60_000);
        return () => window.clearInterval(timer);
    }, [active]);

    useEffect(() => {
        if (!active || !coords) return;
        const controller = new AbortController();
        const refresh = () => {
            if (document.visibilityState === "hidden") return;
            getWeather(coords, controller.signal)
                .then((value) => { if (!controller.signal.aborted) { setWeather(value); setStatus(""); } })
                .catch(() => { if (!controller.signal.aborted) { setWeather(null); setStatus("Local sky unavailable"); } });
        };
        refresh();
        const timer = window.setInterval(refresh, WEATHER_TTL_MS);
        document.addEventListener("visibilitychange", refresh);
        return () => { controller.abort(); window.clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
    }, [active, coords]);

    const requestLocation = () => {
        if (!navigator.geolocation) { setStatus("Location unavailable"); return; }
        setLocating(true);
        setStatus("");
        navigator.geolocation.getCurrentPosition(
            ({ coords: position }) => {
                setCoords({ latitude: position.latitude, longitude: position.longitude });
                setLocating(false);
            },
            () => { setLocating(false); setStatus("Location unavailable"); },
            { enableHighAccuracy: false, maximumAge: WEATHER_TTL_MS, timeout: 10_000 },
        );
    };

    const period = skyPeriod(now, weather);
    const condition = skyCondition(weather);
    const dateLabel = localDateLabel(now, weather?.timezone);

    return (
        <section className="relative h-40 w-full overflow-hidden rounded-xl border border-indigo-300/15 bg-[#080b17] p-5 text-slate-100" aria-label="Sky and greeting">
            <SkyIllustration period={period} condition={condition} cloudCover={weather?.cloudCover || 0} animate={active && pageVisible} />
            <div className="relative z-10 max-w-[68%]">
                <p className="text-xs font-medium text-slate-300">{dateLabel}</p>
                <h2 className="mt-3 text-[21px] font-semibold leading-tight tracking-tight text-slate-100">{greeting(now, weather?.timezone)}</h2>
                <p className="mt-4 text-xs leading-5 text-slate-400">“Same sky, different thoughts.”</p>
                {(locating || status) && <p className="mt-1 text-[10px] text-slate-400">{locating ? "Finding local sky…" : status}</p>}
            </div>
            <div className="absolute bottom-2 right-3 z-10 flex items-center gap-1.5">
                {weather && <a href="https://open-meteo.com/" target="_blank" rel="noreferrer" className="text-[9px] text-slate-500 hover:text-slate-300" title="Weather data by Open-Meteo">Open-Meteo</a>}
                <button type="button" onClick={requestLocation} disabled={locating} className="grid h-6 w-6 place-items-center rounded-md border border-white/10 bg-black/30 text-slate-400 transition hover:border-cyan-300/30 hover:text-cyan-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300 disabled:opacity-50" aria-label="Use my location for local sky" title="Use my location for local sky with Open-Meteo weather">
                    <MapPin size={12} />
                </button>
            </div>
            <span className="sr-only" aria-live="polite">{status || (weather ? `Local weather loaded: ${condition}` : "Time-based sky shown")}</span>
        </section>
    );
}

function QuickLinks({ anonymous }: { anonymous: boolean }) {
    const navigate = useNavigate();
    const links = [
        { label: "Saved Posts", icon: Bookmark, path: anonymous ? null : "/saved", hint: anonymous ? "Sign in to view saved posts" : "Open Saved library" },
        { label: "My Activity", icon: History, path: null, hint: "Activity page is not available yet" },
        { label: "Post Drafts", icon: FilePenLine, path: null, hint: "Post drafts are not available yet" },
        { label: "Find People", icon: UserRoundPlus, path: "/discover?type=people", hint: "Discover people" },
    ];
    return (
        <section className="w-full rounded-xl border border-slate-400/15 bg-[#080c13] p-4 text-slate-100" aria-label="Quick Links">
            <h2 className="text-[15px] font-semibold">Quick Links</h2>
            <div className="mt-4 grid grid-cols-4 gap-1.5">
                {links.map(({ label, icon: Icon, path, hint }) => (
                    <button key={label} type="button" disabled={!path} onClick={() => path && navigate(path)} title={hint} aria-label={path ? label : `${label} — ${hint}`} className="group flex min-w-0 flex-col items-center gap-2 rounded-lg text-center text-slate-300 outline-none transition enabled:hover:text-white enabled:focus-visible:text-white disabled:cursor-not-allowed disabled:opacity-45">
                        <span className="relative grid h-14 w-14 max-w-full place-items-center rounded-xl border border-slate-400/10 bg-[#0c1120] text-slate-300 transition group-hover:border-violet-300/30 group-hover:bg-violet-400/10 group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-cyan-300">
                            <Icon size={22} strokeWidth={1.7} />
                            {!path && <span className="absolute -bottom-1 rounded border border-slate-500/20 bg-[#111625] px-1 text-[8px] leading-3 text-slate-400">{label === "Saved Posts" ? "Sign in" : "Soon"}</span>}
                        </span>
                        <span className="text-[10px] font-medium leading-4">{label}</span>
                    </button>
                ))}
            </div>
        </section>
    );
}

function postAge(value: string, now: Date) {
    const elapsed = Math.max(0, now.getTime() - new Date(value).getTime());
    if (!Number.isFinite(elapsed)) return "Recent post";
    const minutes = Math.floor(elapsed / 60_000);
    if (minutes < 1) return "Posted just now";
    if (minutes < 60) return `Posted ${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Posted ${hours}h ago`;
    const days = Math.floor(hours / 24);
    return days === 1 ? "Posted yesterday" : `Posted ${days}d ago`;
}

async function recentlyActiveSpaces(signal: AbortSignal): Promise<ActiveSpace[]> {
    const joined = new Map<string, Space>();
    let joinedPage = 1;
    while (!signal.aborted) {
        const result = await fetchJoinedSpaces({ archived: false, limit: 100, page: joinedPage });
        if (signal.aborted) return [];
        result.spaces.forEach((space) => {
            if (!space.isArchived && space.canEnter !== false) joined.set(space.id, space);
        });
        if (!result.hasMore || result.spaces.length === 0 || result.page < joinedPage) break;
        joinedPage = result.page + 1;
    }
    if (signal.aborted || joined.size === 0) return [];

    const latest = new Map<string, ActiveSpace>();
    const seenCursors = new Set<string>();
    let cursor: string | null = null;
    while (!signal.aborted) {
        const discovered: DiscoverResponse = await fetchDiscover({ type: "posts", sort: "newest", limit: 36, cursor, signal });
        if (signal.aborted) return [];
        for (const item of discovered.items) {
            if (item.kind !== "post" || !item.post) continue;
            const post = item.post;
            const spaceId = String(post.space?.id || item.space?.id || "");
            const space = joined.get(spaceId);
            if (!space) continue;
            // Discover applies author/block checks; only include accessible joined Spaces.
            if (post.isExclusive || (post.visibility && !["public", "space"].includes(post.visibility))) continue;
            const lastPostAt = String(post.createdAt || item.createdAt || "");
            const postTime = new Date(lastPostAt).getTime();
            if (!Number.isFinite(postTime) || postTime > Date.now()) continue;
            const previous = latest.get(spaceId);
            if (!previous || postTime > new Date(previous.lastPostAt).getTime()) {
                latest.set(spaceId, { id: spaceId, title: space.title, lastPostAt });
            }
        }
        // The newest-first source needs no further pages once every joined Space has an entry.
        const nextCursor: string | null = discovered.nextCursor;
        if (latest.size === joined.size || !discovered.hasMore || !nextCursor || seenCursors.has(nextCursor)) break;
        seenCursors.add(nextCursor);
        cursor = nextCursor;
    }
    return Array.from(latest.values()).sort((a, b) => new Date(b.lastPostAt).getTime() - new Date(a.lastPostAt).getTime());
}

function RecentlyActive({ active, anonymous, revision }: { active: boolean; anonymous: boolean; revision: number }) {
    const navigate = useNavigate();
    const [spaces, setSpaces] = useState<ActiveSpace[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [now, setNow] = useState(() => new Date());
    const listRef = useRef<HTMLDivElement>(null);
    const [moreBelow, setMoreBelow] = useState(false);
    const updateScrollFade = useCallback(() => {
        const list = listRef.current;
        setMoreBelow(Boolean(list && list.scrollHeight > list.clientHeight + 1 && list.scrollHeight - list.clientHeight - list.scrollTop > 1));
    }, []);

    useEffect(() => {
        if (!active || anonymous) { setSpaces([]); setLoading(false); setError(false); return; }
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        recentlyActiveSpaces(controller.signal)
            .then((result) => { if (!controller.signal.aborted) setSpaces(result); })
            .catch(() => { if (!controller.signal.aborted) { setSpaces([]); setError(true); } })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [active, anonymous, revision]);

    useEffect(() => {
        const list = listRef.current;
        if (!list) { setMoreBelow(false); return; }
        list.scrollTop = 0;
        updateScrollFade();
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateScrollFade);
        observer?.observe(list);
        window.addEventListener("resize", updateScrollFade);
        return () => { observer?.disconnect(); window.removeEventListener("resize", updateScrollFade); };
    }, [active, spaces, updateScrollFade]);

    useEffect(() => {
        if (!active) return;
        setNow(new Date());
        const timer = window.setInterval(() => setNow(new Date()), 60_000);
        return () => window.clearInterval(timer);
    }, [active]);

    return (
        <section className="min-h-48 w-full rounded-xl border border-slate-400/15 bg-[#080c13] p-4 text-slate-100" aria-label="Recently Active Spaces">
            <div className="flex items-center justify-between gap-2">
                <h2 className="text-[15px] font-semibold">Recently Active</h2>
                <button type="button" onClick={() => navigate(anonymous ? "/spaces/all" : "/spaces")} className="inline-flex items-center gap-1 text-xs font-medium text-violet-300 transition hover:text-violet-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300">See all <ArrowRight size={13} /></button>
            </div>
            {spaces.length > 0 ? (
                <div className="relative mt-3">
                    <div ref={listRef} className="bs-home-sidebar-active-list space-y-1" onScroll={updateScrollFade} tabIndex={0} role="region" aria-label="Recently active Spaces list">
                    {spaces.map((space, index) => (
                        <button key={space.id} type="button" onClick={() => navigate(`/spaces/${encodeURIComponent(space.id)}`)} className="group flex w-full items-center gap-3 rounded-lg px-1.5 py-1.5 text-left transition hover:bg-white/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300">
                            <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl border text-base font-semibold ${["border-violet-300/10 bg-violet-400/15 text-violet-200", "border-amber-300/10 bg-amber-400/15 text-amber-100", "border-cyan-300/10 bg-cyan-400/15 text-cyan-200"][index % 3]}`}>{space.title.slice(0, 1).toUpperCase() || <Orbit size={18} />}</span>
                            <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-slate-200 group-hover:text-white">{space.title}</span><span className="block text-xs text-slate-400">{postAge(space.lastPostAt, now)}</span></span>
                        </button>
                    ))}
                    </div>
                    {moreBelow && <div className="bs-home-sidebar-active-fade" aria-hidden="true" />}
                </div>
            ) : (
                <p className="mt-7 text-xs leading-5 text-slate-400">{loading ? "Finding recent posts…" : error ? "Space activity is unavailable right now." : anonymous ? "Explore Spaces to find your conversations." : "No recent posts to surface from your Spaces."}</p>
            )}
        </section>
    );
}

export default function HomeSidebar({ active, anonymous, revision }: { active: boolean; anonymous: boolean; revision: number }) {
    const visible = useDesktopSidebar(active);
    return (
        <aside className="bs-home-sidebar stealth-scroll hidden flex-col gap-3 self-start xl:sticky xl:top-4 xl:flex xl:max-h-[calc(100vh-7rem)] xl:w-full xl:overflow-y-auto xl:pr-1" aria-label="Feed sidebar">
            <SkyGreeting active={visible} />
            <QuickLinks anonymous={anonymous} />
            <RecentlyActive active={visible} anonymous={anonymous} revision={revision} />
        </aside>
    );
}
